import { Button, Typography } from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";

import {
  ExportAudienceMutation,
  ExportAudienceMutationVariables,
  GetAudienceExportSignedDownloadUrlMutation,
  GetAudienceExportSignedDownloadUrlMutationVariables,
  GetAudienceQuery,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import {
  EXPORT_AUDIENCE,
  GET_AUDIENCE_EXPORT_SIGNED_DOWNLOAD_URL,
} from "~frontend/graphql/audience";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

type AudienceForExport = NonNullable<GetAudienceQuery["getAudience"]>;

function useAudienceExportState(
  audience: AudienceForExport
): "idle" | "generating" | "ready" {
  const job = audience.exportJob;

  if (!job) {
    return "idle";
  }

  if (jobInProgress(job)) {
    return "generating";
  }

  return "ready";
}

export function AudienceExportButton({
  audience,
  buttonType,
  setErrorMessage,
}: {
  audience: AudienceForExport;
  buttonType?: "REFRESH_LINK" | "DEFAULT";
  setErrorMessage?: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
}): ReactElement {
  const [downloadState, setDownloadState] = useState<
    | { mode: "idle" }
    | { mode: "loading" }
    | { mode: "loaded"; url: string }
    | { mode: "error"; error: string }
  >({ mode: "idle" });
  const exportState = useAudienceExportState(audience);

  const formatError = useErrorMessageFormatter();
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  const [getAudienceExportSignedDownloadUrl] = useMutation<
    GetAudienceExportSignedDownloadUrlMutation,
    GetAudienceExportSignedDownloadUrlMutationVariables
  >(GET_AUDIENCE_EXPORT_SIGNED_DOWNLOAD_URL, {
    variables: {
      input: {
        audienceId: audience.id,
      },
    },
  });

  const [exportAudienceMutation] = useMutation<
    ExportAudienceMutation,
    ExportAudienceMutationVariables
  >(EXPORT_AUDIENCE, {
    onErrorSnackbarMessage: "Failed to start audience export.",
    variables: {
      input: {
        audienceId: audience.id,
      },
    },
  });

  async function downloadDataExport() {
    setDownloadState({ mode: "loading" });
    const result = await getAudienceExportSignedDownloadUrl();
    if (result.errors) {
      setDownloadState({ mode: "error", error: result.errors.join(", ") });
      throw new Error(formatError(result.errors));
    }
    setDownloadState({
      mode: "loaded",
      url: result.data!.getAudienceExportSignedDownloadUrl,
    });
  }

  const label = {
    idle: "Generate Export",
    generating: "Exporting...",
    ready: "Download Export",
  };

  const handleClick = {
    idle: () => {
      void exportAudienceMutation();
    },
    generating: () => {},
    ready: downloadDataExport,
  };

  useEffect(() => {
    if (downloadState.mode === "loaded") {
      if (setErrorMessage) {
        setErrorMessage(null);
      }
      linkRef.current?.click();
      setTimeout(() => {
        setDownloadState({ mode: "idle" });
      }, 2000); // To fudge the fact that the button's state changes before the download completes.
    }
    if (downloadState.mode === "error" && setErrorMessage) {
      setErrorMessage(downloadState.error);
    }
  }, [downloadState, setErrorMessage]);

  const button: ReactElement<typeof Button> =
    buttonType === "REFRESH_LINK" ? (
      <LinkButton
        onClick={handleClick["idle"]}
        sx={{
          display: exportState === "ready" ? "auto" : "none",
          textWrap: "nowrap",
          color: palette.darkGray,
          textDecorationColor: palette.darkGray,
        }}
      >
        Refresh Export
      </LinkButton>
    ) : ["loading", "loaded"].includes(downloadState.mode) ? (
      <FlexBox direction="column" alignItems="flex-end">
        <Typography
          sx={{
            textTransform: "uppercase",
            fontWeight: "bold",
            color: palette.darkGray,
            textAlign: "right",
          }}
        >
          Downloading...
        </Typography>
        <Typography sx={{ textAlign: "right" }}>
          {`Your download will begin shortly. If it does not, `}
          <LinkButton
            onClick={handleClick["ready"]}
            sx={{ display: "inline", textWrap: "nowrap" }}
          >
            click here
          </LinkButton>
          .
        </Typography>
      </FlexBox>
    ) : (
      <Button
        variant="contained"
        onClick={handleClick[exportState]}
        disabled={exportState === "generating"}
      >
        {label[exportState]}
      </Button>
    );

  return (
    <div>
      {button}
      {downloadState.mode === "loaded" && (
        <a ref={linkRef} href={downloadState.url} aria-disabled={true} />
      )}
    </div>
  );
}
